import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, MapPin } from "lucide-react";
import "./TourPortal.css";
import stateBoundaries from "../data/tourStates.json";
import { PRIMARY_TOUR, UNLOCK_TOUR } from "../data/tourSchedule";

// Project the same geographic coordinates used by the verified 48-state + DC
// vector boundary layer. All marker points are checked against their states.
// Continental USA: longitude -125..-63.57, latitude 50..23.16.
export const mapProject = (lon, lat) => ({
  x: ((lon + 125) * 14 / 860) * 100,
  y: ((50 - lat) * 19 / 510) * 100
});
const COORDS = {
  "Houston": {
    "state": "Texas",
    "lon": -95.3698,
    "lat": 29.7604
  },
  "Orlando": {
    "state": "Florida",
    "lon": -81.3792,
    "lat": 28.5383
  },
  "Dallas": {
    "state": "Texas",
    "lon": -96.797,
    "lat": 32.7767
  },
  "Atlanta": {
    "state": "Georgia",
    "lon": -84.388,
    "lat": 33.749
  },
  "Charlotte": {
    "state": "North Carolina",
    "lon": -80.8431,
    "lat": 35.2271
  },
  "Chicago": {
    "state": "Illinois",
    "lon": -87.6298,
    "lat": 41.8781
  },
  "Detroit": {
    "state": "Michigan",
    "lon": -83.0458,
    "lat": 42.3314
  },
  "Seattle": {
    "state": "Washington",
    "lon": -122.3321,
    "lat": 47.6062
  },
  "Philadelphia": {
    "state": "Pennsylvania",
    "lon": -75.1652,
    "lat": 39.9526
  },
  "New York": {
    "state": "New York",
    "lon": -74.006,
    "lat": 40.7128
  },
  "Los Angeles": {
    "state": "California",
    "lon": -118.2437,
    "lat": 34.0522
  },
  "New Orleans": {
    "state": "Louisiana",
    "lon": -90.0715,
    "lat": 29.9511
  },
  "Austin": {
    "state": "Texas",
    "lon": -97.7431,
    "lat": 30.2672
  },
  "Tampa": {
    "state": "Florida",
    "lon": -82.4572,
    "lat": 27.9506
  },
  "San Antonio": {
    "state": "Texas",
    "lon": -98.4936,
    "lat": 29.4241
  },
  "Birmingham": {
    "state": "Alabama",
    "lon": -86.8025,
    "lat": 33.5207
  },
  "Raleigh": {
    "state": "North Carolina",
    "lon": -78.6382,
    "lat": 35.7796
  },
  "Milwaukee": {
    "state": "Wisconsin",
    "lon": -87.9065,
    "lat": 43.0389
  },
  "Cleveland": {
    "state": "Ohio",
    "lon": -81.6944,
    "lat": 41.4993
  },
  "Portland": {
    "state": "Oregon",
    "lon": -122.6765,
    "lat": 45.5231
  },
  "Baltimore": {
    "state": "Maryland",
    "lon": -76.6122,
    "lat": 39.2904
  },
  "Boston": {
    "state": "Massachusetts",
    "lon": -71.0589,
    "lat": 42.3601
  },
  "San Diego": {
    "state": "California",
    "lon": -117.1611,
    "lat": 32.7157
  },
  "Baton Rouge": {
    "state": "Louisiana",
    "lon": -91.1403,
    "lat": 30.4515
  }
};
export const TOUR_RADAR_CITIES = [...PRIMARY_TOUR, ...UNLOCK_TOUR].map((city) => ({
  name: city.city, ...COORDS[city.city], founding: city.active === true,
  conditional: city.conditional === true, region: city.status,
}));
const HOME = { name: "New Orleans", lon: -90.0715, lat: 29.9511 };
export default function TourRadarMap({ cities, getHref, onFocusCity }) {
  const viewport = useRef(null);
  const [view, setView] = useState("primary");
  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 900 && viewport.current) {
      // Start mobile users on the founding-city region, not empty Pacific space.
      viewport.current.scrollLeft = 280;
    }
  }, []);

  const statusByCity = Object.fromEntries(cities.map((city) => [city.city, city]));
  const visiblePins = TOUR_RADAR_CITIES.filter((pin) => view === "all" || (view === "unlock" ? pin.conditional || pin.founding : !pin.conditional));

  return (
    <section aria-labelledby="tour-radar-title" className="relative mt-12 overflow-hidden rounded-[1.8rem] border border-white/15 bg-[#101114]">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-white/10 px-5 py-6 md:px-9">
        <div>
          <p className="font-accent text-[11px] tracking-[.25em] text-[#39E75F]">THE TOUR RADAR</p>
          <h3 id="tour-radar-title" className="mt-2 font-display text-3xl tracking-tight sm:text-5xl">PICK YOUR CITY ON THE MAP.</h3>
          <p className="mt-2 max-w-xl text-sm leading-6 text-white/55">Houston and Chicago represent the founding cities of the South and North. Explore the 12 monthly targets, then switch to the sell-out-only cities. Pin positions are geographic; dates are proposed.</p>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-[11px] font-accent tracking-[.10em]">
          <span className="flex items-center gap-2 text-white/70"><span className="h-2.5 w-2.5 rounded-full bg-[#39E75F]"/> FOUNDING CITIES · HOUSTON + CHICAGO</span>
          <span className="flex items-center gap-2 text-white/55"><span className="h-2.5 w-2.5 rounded-full border border-white/60"/> MONTHLY TARGETS</span>
          <span className="flex items-center gap-2 text-[#e8bd84]"><span className="h-2.5 w-2.5 rounded-full bg-[#e8bd84]"/> CONDITIONAL UNLOCKS</span>
        </div>
      </div>

      <div role="group" aria-label="Map city display" className="flex flex-wrap gap-2 border-b border-white/10 px-5 py-3 md:px-9">
        {[[ "primary", "MONTHLY TOUR" ], [ "unlock", "UNLOCK CITIES" ], [ "all", "ALL 24" ]].map(([value, label]) => <button type="button" key={value} onClick={() => setView(value)} aria-pressed={view === value} className={`min-h-11 rounded-full border px-4 py-2 font-accent text-[11px] tracking-wider transition ${view === value ? "border-[#39E75F] bg-[#39E75F] text-black" : "border-white/20 text-white/65 hover:border-white/50"}`}>{label}</button>)}
      </div>
      <div ref={viewport} role="region" tabIndex={0} aria-label="Interactive tour map; scroll horizontally on narrow screens" className="overflow-x-auto overscroll-x-contain [scrollbar-width:thin] [scrollbar-color:#3f4746_transparent]">
        <div className="tour-radar-world relative w-[860px] min-w-[860px] md:w-full" style={{aspectRatio:"860 / 510"}}>
          <div className="pointer-events-none absolute inset-0 opacity-[.22]" style={{ backgroundImage:"linear-gradient(rgba(245,240,232,.09) 1px,transparent 1px),linear-gradient(90deg,rgba(245,240,232,.09) 1px,transparent 1px)",backgroundSize:"52px 52px" }}/>
          <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 860 510" preserveAspectRatio="xMidYMid meet" aria-label="Geographically aligned contiguous United States with accurate state borders and tour city locations" role="img">
            <title>Real state-border map of the contiguous United States, with Houston and Chicago highlighted as founding cities</title>
            <g strokeWidth="1" strokeLinejoin="round">
              {stateBoundaries.map((s) => <path key={s.name} d={s.d} fill={s.name === "Texas" ? "#361b20" : s.name === "Illinois" ? "#1b2c4f" : "#1c2429"} stroke={s.name === "Texas" ? "#E32636" : s.name === "Illinois" ? "#6b9cff" : "rgba(245,240,232,.28)"}>
                <title>{s.name + (s.name === "Texas" ? " · Houston founding city" : s.name === "Illinois" ? " · Chicago founding city" : "")}</title>
              </path>)}
            </g>
            <g transform={`translate(${(HOME.lon + 125) * 14} ${(50 - HOME.lat) * 19})`} aria-label="New Orleans, origin city">
              <circle r="11" fill="rgba(245,240,232,.13)" />
              <circle r="4" fill="#F5F0E8" />
              <text x="11" y="21" fill="#F5F0E8" fontSize="11" fontFamily="Inter, sans-serif">NEW ORLEANS · HOME</text>
            </g>
          </svg>
          <div className="pointer-events-none absolute left-6 top-5 font-accent text-[10px] tracking-[.17em] text-white/35">US TOUR OUTLOOK / 2027</div>
          {visiblePins.map((pin) => {
            const c = statusByCity[pin.name];
            const position = mapProject(pin.lon, pin.lat);
            if (!c) return null;
            const base = "group absolute z-10 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition-transform focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#39E75F] hover:scale-110";
            const marker = pin.founding
              ? <><span className="tour-portal-ring" aria-hidden="true" /><span className="tour-portal-shaft" aria-hidden="true" /><span className="tour-portal-polaroid" aria-hidden="true"><img src={pin.name === "Houston" ? "/campaign-assets/party-red.webp" : "/campaign-assets/party-blue.webp"} alt="" loading="eager"/><span className="tour-portal-label">{pin.name.toUpperCase()} <ArrowUpRight size={11}/></span></span></>
              : <><span className={`grid place-items-center rounded-full border text-white shadow-[0_6px_24px_rgba(0,0,0,.7)] ${pin.conditional ? "h-8 w-8 border-[#e8bd84]/80 bg-[#41301d]" : "h-10 w-10 border-white/65 bg-[#202325]"}`}><MapPin size={pin.conditional ? 14 : 17} strokeWidth={2.5} aria-hidden="true"/></span><span className="pointer-events-none absolute bottom-[45px] rounded-lg border border-white/15 bg-black/90 px-2.5 py-1.5 text-xs text-white opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 whitespace-nowrap">{pin.name}</span></>;
            const enterCity = (event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
              if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
              event.preventDefault();
              event.currentTarget.classList.add("is-entering");
              window.setTimeout(() => window.location.assign(getHref(pin.name)), 540);
            };
            return pin.founding
              ? <a key={pin.name} data-tour-pin={pin.name} href={getHref(pin.name)} onClick={enterCity} aria-label={pin.name + (pin.name === "Chicago" ? " founding city of the North" : " founding city of the South") + ": open its tour landing page"} title={"Explore " + pin.name} className={base + " tour-portal-anchor"} style={{left:position.x+"%",top:position.y+"%"}}>{marker}</a>
              : <button key={pin.name} data-tour-pin={pin.name} type="button" onClick={()=>onFocusCity(pin.name)} aria-label={pin.name + (pin.conditional ? ": join interest for conditional sell-out city" : ": select proposed tour market")} title={"Explore " + pin.name} className={base} style={{left:position.x+"%",top:position.y+"%"}}>{marker}</button>;
          })}
          <div className="pointer-events-none absolute bottom-4 left-5 rounded-md bg-[#090909]/80 px-3 py-2 font-accent text-[10px] tracking-[.1em] text-white/65">REAL STATE BOUNDARIES · TARGETS NOT CONFIRMED</div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 border-t border-white/10 px-5 py-4 text-xs text-white/50 md:px-9">
        <span>{view === "all" ? "24 possible cities" : view === "unlock" ? "12 conditional candidates + 2 founders" : "12 monthly city targets"} · Houston + Chicago: founding cities</span>
        <span className="font-accent tracking-[.14em]">SWIPE TO EXPLORE <span aria-hidden="true">→</span></span>
      </div>
    </section>
  );
}
